import type { AppInventoryOptionType } from '@openx/types/targeting/appInventory.ts';
import {
  AppInventoryType,
  InventoryContentOption,
  type PublisherInventoryRuleOptions,
} from '@openx/types/targeting/inventoryAndContent.ts';
import { ComparisonType } from '@openx/types/targeting/targetingValuesTypes.ts';
import type { Dispatch, SetStateAction } from 'react';
import { useMemo, useState } from 'react';
import type {
  SetAppInventoryTypeDispatch,
  SetComparisonTypeDispatch,
} from '../../../InventoryAndContent/InventoryAndContentTopBox/constants.ts';
import { publisherInventoryRuleOptionsInitialState } from '../../../InventoryAndContent/InventoryAndContentTopBox/constants.ts';
import { useAppInventoryAllowed } from '../featureFlags.ts';

export type UseAppInventoryTopBoxProps<TContentOption extends AppInventoryOptionType | InventoryContentOption> = {
  comparisonType: ComparisonType;
  contentOption: TContentOption;
  appInventoryTypeProp?: AppInventoryType;
  setAppInventoryTypeProp?: SetAppInventoryTypeDispatch;
  appNameComparisonTypeProp?: ComparisonType;
  setAppNameComparisonTypeProp?: SetComparisonTypeDispatch;
  bundleIdComparisonTypeProp?: ComparisonType;
  setBundleIdComparisonTypeProp?: SetComparisonTypeDispatch;
};

export type UseAppInventoryTopBoxReturn<TContentOption extends AppInventoryOptionType | InventoryContentOption> = {
  effectiveContentOption: TContentOption;
  effectiveComparisonType: ComparisonType;
  publisherInventoryRuleOptions: PublisherInventoryRuleOptions;
  setPublisherInventoryRuleOptions: Dispatch<SetStateAction<PublisherInventoryRuleOptions>>;
  appInventoryType: AppInventoryType;
  setAppInventoryType: Dispatch<SetStateAction<AppInventoryType>>;
  appNameComparisonType: ComparisonType;
  bundleIdComparisonType: ComparisonType;
};

export const useAppInventoryTopBox = <TContentOption extends AppInventoryOptionType | InventoryContentOption>({
  comparisonType,
  contentOption,
  appInventoryTypeProp,
  setAppInventoryTypeProp,
  appNameComparisonTypeProp,
  setAppNameComparisonTypeProp,
  bundleIdComparisonTypeProp,
  setBundleIdComparisonTypeProp,
}: UseAppInventoryTopBoxProps<TContentOption>): UseAppInventoryTopBoxReturn<TContentOption> => {
  // App Inventory - use props from parent if provided, otherwise use local state (for backward compatibility)
  const isAppInventoryAllowed = useAppInventoryAllowed();

  const [localAppInventoryType, setLocalAppInventoryType] = useState<AppInventoryType>(AppInventoryType.APP_NAME);
  const [localAppNameComparisonType, setLocalAppNameComparisonType] = useState<ComparisonType>(ComparisonType.EQUALS);
  const [localBundleIdComparisonType, setLocalBundleIdComparisonType] = useState<ComparisonType>(ComparisonType.EQUALS);

  // Use parent props if provided, otherwise use local state
  const appInventoryType = appInventoryTypeProp ?? localAppInventoryType;
  const setAppInventoryType = setAppInventoryTypeProp ?? setLocalAppInventoryType;
  const appNameComparisonType = appNameComparisonTypeProp ?? localAppNameComparisonType;
  const _setAppNameComparisonType = setAppNameComparisonTypeProp ?? setLocalAppNameComparisonType;
  const bundleIdComparisonType = bundleIdComparisonTypeProp ?? localBundleIdComparisonType;
  const _setBundleIdComparisonType = setBundleIdComparisonTypeProp ?? setLocalBundleIdComparisonType;

  const [publisherInventoryRuleOptions, setPublisherInventoryRuleOptions] = useState<PublisherInventoryRuleOptions>(
    publisherInventoryRuleOptionsInitialState
  );

  // For App Inventory, use the appropriate comparison type based on selected type
  const effectiveComparisonType = useMemo(() => {
    if (isAppInventoryAllowed && contentOption === InventoryContentOption.APP_GROUP) {
      return appInventoryType === AppInventoryType.APP_NAME ? appNameComparisonType : bundleIdComparisonType;
    }
    return comparisonType;
  }, [
    isAppInventoryAllowed,
    contentOption,
    appInventoryType,
    appNameComparisonType,
    bundleIdComparisonType,
    comparisonType,
  ]);

  // For App Inventory, use the appropriate content option based on selected type
  const effectiveContentOption = useMemo(() => {
    if (isAppInventoryAllowed && contentOption === InventoryContentOption.APP_GROUP) {
      return (
        appInventoryType === AppInventoryType.APP_NAME
          ? InventoryContentOption.APP_GROUP
          : InventoryContentOption.APP_BUNDLE_ID
      ) as TContentOption;
    }
    return contentOption;
  }, [isAppInventoryAllowed, contentOption, appInventoryType]);

  return {
    effectiveContentOption,
    effectiveComparisonType,
    publisherInventoryRuleOptions,
    setPublisherInventoryRuleOptions,
    appInventoryType,
    setAppInventoryType,
    appNameComparisonType,
    bundleIdComparisonType,
  };
};
